import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { User } from '../types';
import { normalizePaletteIndex } from './bottAvatar';

// FakePresence — ambient room population from public.fake_room_presence.
//
// Companion to PresenceChannel:
//   - PresenceChannel handles REAL signed-in humans via Supabase Realtime
//     presence (ephemeral, in-memory, peer-to-peer per channel).
//   - FakePresence handles FAKE ambient users via a durable table + an
//     RPC reconciler (see supabase/migrations/20260521000002_…).
//
// Each row in fake_room_presence is normalized to a FakeUserRecord whose
// `id` is prefixed with `fake:` so the rest of the app can identify fake
// users instantly without a DB roundtrip — important for guarding
// account-only affordances (profile, follow, DJ panel).
//
// The reconciler is anon-callable, so signed-out visitors keep the room
// warm just by sitting on the page. Each tab calls it on a ~22s + jitter
// tick; the function self-throttles in PL/pgSQL so concurrent callers
// don't pile on.

const FAKE_ID_PREFIX = 'fake:';

/** Local userId convention for fake users. Prefix is checked at every
 *  boundary that branches on real-vs-fake (e.g. AgentPopover open). */
export function isFakeUserId(id: string | undefined | null): boolean {
  return !!id && id.startsWith(FAKE_ID_PREFIX);
}

export interface FakeUserRecord {
  /** Local userId: `fake:{fake_users.id}`. Unique across real + fake. */
  id: string;
  /** Raw uuid from public.fake_users — useful if a future feature needs
   *  to join back to the source table (vibe, genres, eligibility). */
  fakeUserId: string;
  alias: string;
  avatarSeed: string;
  avatarPaletteIndex: number;
  vibe?: string;
  preferredGenres?: string[];
  energyLevel?: string;
  isHouseDjEligible: boolean;
  isCurrentHouseDj: boolean;
  joinedAt: number;
  plannedLeaveAt: number | null;
}

/** Project a FakeUserRecord into the shared User shape used by the room
 *  orchestrator + PresenceTokens. Real and fake users render through the
 *  same pipeline; the `fake:` id prefix is the only signal downstream
 *  code needs to gate account-only behavior. */
export function fakeUserAsUser(f: FakeUserRecord): User {
  return {
    id: f.id,
    handle: f.alias,
    displayName: f.alias,
    avatarSeed: f.avatarSeed,
    avatarPaletteIndex: f.avatarPaletteIndex,
    role: 'crowd',
    joinedAt: f.joinedAt,
  };
}

export type FakePresenceListener = (fakeUsers: FakeUserRecord[]) => void;

/** How often each tab calls the reconciler RPC. The DB function throttles
 *  internally at ~8s, so multiple open tabs converge on one effective
 *  tick per ~8s without coordinated scheduling. */
const RECONCILE_INTERVAL_MS = 22_000;
/** Random jitter added per tick so two tabs that opened in the same RAF
 *  don't fire RPCs in lockstep — helpful for keeping the DB throttle
 *  windows distinct. */
const RECONCILE_JITTER_MS = 6_000;

// Row shape returned by the joined select below. Kept private — callers
// only ever see the normalized FakeUserRecord.
interface JoinedRow {
  id: string;
  fake_user_id: string;
  room_id: string;
  joined_at: string;
  last_seen_at: string;
  planned_leave_at: string | null;
  current_state: string;
  is_current_house_dj: boolean;
  // !inner join — fake_users is guaranteed present (FK + not-null FK col).
  // Supabase typings model nested rows as an object for non-array FKs.
  fake_users: {
    id: string;
    alias: string;
    avatar_seed: string;
    avatar_palette_index: number;
    vibe: string | null;
    preferred_genres: string[] | null;
    energy_level: string | null;
    is_house_dj_eligible: boolean;
  };
}

export class FakePresence {
  private roomId: string;
  private listeners = new Set<FakePresenceListener>();
  private records = new Map<string, FakeUserRecord>();
  private channel: RealtimeChannel | null = null;
  private reconcileTimer: number | null = null;
  private started = false;
  private destroyed = false;

  constructor(roomId: string) {
    this.roomId = roomId;
  }

  async start(): Promise<void> {
    if (this.started || this.destroyed) return;
    this.started = true;
    if (!supabase) {
      // Local-only mode (no Supabase env): the room renders without
      // ambient fakes. Real users still flow through PresenceChannel.
      return;
    }
    await this.refresh();
    this.subscribe();
    // Kick the first reconcile immediately (after a tiny jitter) so a
    // visitor landing on a cold room sees the population fill within
    // seconds, not after one full RECONCILE_INTERVAL_MS.
    this.scheduleNextReconcile(0);
  }

  stop(): void {
    this.destroyed = true;
    if (this.reconcileTimer !== null) {
      clearTimeout(this.reconcileTimer);
      this.reconcileTimer = null;
    }
    if (this.channel && supabase) supabase.removeChannel(this.channel);
    this.channel = null;
    this.listeners.clear();
  }

  /** Subscribe to fake-presence snapshots. Fires immediately with the
   *  current cached state, then on every change. */
  onSync(cb: FakePresenceListener): () => void {
    this.listeners.add(cb);
    cb(Array.from(this.records.values()));
    return () => { this.listeners.delete(cb); };
  }

  /** All currently active fake users flagged as eligible to stand in
   *  as house DJ. Extension point for future rotation logic — see the
   *  comment in src/room/main.ts where the eligible pool is consumed. */
  getEligibleHouseDJs(): FakeUserRecord[] {
    return Array.from(this.records.values()).filter((r) => r.isHouseDjEligible);
  }

  /** The single fake user (if any) currently flagged as the active house
   *  DJ in this room. Drives the "Now DJing" identity surface when no
   *  real DJ is at the booth — when wired up. */
  getCurrentHouseDJ(): FakeUserRecord | null {
    for (const r of this.records.values()) {
      if (r.isCurrentHouseDj) return r;
    }
    return null;
  }

  // -------------------------------------------------------- internals

  private async refresh(): Promise<void> {
    if (!supabase || this.destroyed) return;
    const { data, error } = await supabase
      .from('fake_room_presence')
      .select(
        // Inner join + nested fake_users so one round-trip hydrates both
        // the presence row and the persona. Order doesn't matter; the
        // client lays tokens out via PresenceTokens' deterministic hash.
        'id, fake_user_id, room_id, joined_at, last_seen_at, planned_leave_at, current_state, is_current_house_dj, ' +
          'fake_users!inner ( id, alias, avatar_seed, avatar_palette_index, vibe, preferred_genres, energy_level, is_house_dj_eligible )',
      )
      .eq('room_id', this.roomId);
    if (error) {
      // Stringify so the dev console shows the Supabase status/message
      // instead of the default '[object Object]'. Most common cause is
      // "migration not applied yet" (relation does not exist).
      console.warn('[fakePresence] refresh failed:', error.message ?? error, error);
      return;
    }
    const rows = (data ?? []) as unknown as JoinedRow[];
    const next = new Map<string, FakeUserRecord>();
    for (const r of rows) {
      const rec = rowToRecord(r);
      next.set(rec.id, rec);
    }
    this.records = next;
    this.emit();
  }

  private subscribe(): void {
    if (!supabase || this.destroyed) return;
    this.channel = supabase
      .channel(`fake_presence:${this.roomId}`)
      .on(
        // postgres_changes covers INSERT / UPDATE / DELETE on the table.
        // We don't need the payload itself — a single re-fetch keeps the
        // local cache + nested fake_users hydration trivial.
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fake_room_presence',
          filter: `room_id=eq.${this.roomId}`,
        },
        () => { void this.refresh(); },
      )
      .subscribe();
  }

  private scheduleNextReconcile(initialDelay: number): void {
    if (this.destroyed) return;
    const jitter = Math.floor(Math.random() * RECONCILE_JITTER_MS);
    this.reconcileTimer = window.setTimeout(() => {
      void this.tickReconcile().finally(() => {
        this.scheduleNextReconcile(RECONCILE_INTERVAL_MS);
      });
    }, initialDelay + jitter);
  }

  private async tickReconcile(): Promise<void> {
    if (!supabase || this.destroyed) return;
    try {
      const { error } = await supabase.rpc('reconcile_main_booth_presence', {
        p_room_id: this.roomId,
      });
      if (error) {
        // Don't escalate — the room still functions with whatever roster
        // was last fetched. Log so a real outage shows up in the console.
        console.warn('[fakePresence] reconcile rpc error:', error.message ?? error, error);
      }
    } catch (err) {
      console.warn('[fakePresence] reconcile threw', err);
    }
  }

  private emit(): void {
    const snap = Array.from(this.records.values());
    for (const l of this.listeners) l(snap);
  }
}

function rowToRecord(r: JoinedRow): FakeUserRecord {
  return {
    id: `${FAKE_ID_PREFIX}${r.fake_user_id}`,
    fakeUserId: r.fake_user_id,
    alias: r.fake_users.alias,
    avatarSeed: r.fake_users.avatar_seed,
    avatarPaletteIndex: normalizePaletteIndex(r.fake_users.avatar_palette_index),
    vibe: r.fake_users.vibe ?? undefined,
    preferredGenres: r.fake_users.preferred_genres ?? undefined,
    energyLevel: r.fake_users.energy_level ?? undefined,
    isHouseDjEligible: r.fake_users.is_house_dj_eligible,
    isCurrentHouseDj: r.is_current_house_dj,
    joinedAt: Date.parse(r.joined_at),
    plannedLeaveAt: r.planned_leave_at ? Date.parse(r.planned_leave_at) : null,
  };
}
